import { useCallback } from "react";

import {
  downloadManagerPreferenceFormatFavoritesState,
  downloadManagerPreferenceLastUsedFormatsState,
  FormatType,
  SentryBreadcrumbCategory,
} from "@keepeek/commons";
import * as Sentry from "@sentry/nextjs";
import uniq from "lodash/uniq";
import { SetterOrUpdater, useRecoilState } from "recoil";

export type UseFormatFavorites = {
  lastUsed: FormatType["id"][];
  setLastUsed: SetterOrUpdater<FormatType["id"][]>;
  favorites: FormatType["id"][];
  setFavorites: SetterOrUpdater<FormatType["id"][]>;
  selectFavorite: (id: FormatType["id"]) => void;
  selectFavoritesByDownload: (ids: FormatType["id"][]) => void;
};

export const useFormatFavorites = (): UseFormatFavorites => {
  const [favorites, setFavorites] = useRecoilState(downloadManagerPreferenceFormatFavoritesState);

  const selectFavorite = useCallback(
    (id: FormatType["id"]) => {
      setFavorites((ids) => {
        if (ids.includes(id)) {
          return uniq(ids.filter((i) => i !== id));
        }
        return uniq([...ids, id]);
      });

      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.USER_INTERACTION,
        message: `useFormatFavorites - selectFavorite: ${id}`,
      });
    },
    [setFavorites],
  );

  const [lastUsed, setLastUsed] = useRecoilState(downloadManagerPreferenceLastUsedFormatsState);

  const selectFavoritesByDownload = useCallback(
    (ids: FormatType["id"][]) => {
      setFavorites((prevIds) => uniq([...prevIds, ...ids]));
      setLastUsed(ids);
    },
    [setFavorites, setLastUsed],
  );

  return {
    lastUsed,
    setLastUsed,
    favorites,
    setFavorites,
    selectFavorite,
    selectFavoritesByDownload,
  };
};
